import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { SOFT_SIGNALS, SoftSignal } from 'models/Lead';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import UnsureSignalModal from '../UnsureSignalModal';
import DislikeSignalModal from '../DislikeSignalModal';
import api from 'api';
import { useTranslation } from 'react-i18next';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { SoftSignalInitiatedEventProps } from 'models/MixpanelEvents';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import SoftSignalButton from '../SoftSignalButton';
import type { SequenceItem } from '../../DataroomNodeViewModal.component';

import './SoftSignals.styles.scss';
import './SoftSignals.styles.responsive.scss';

type SoftSignalsProps = {
  className?: string;
  dataRoomId: string;
  dataRoomNodeId: string;
  dealRoomId: string;
  lastSoftSignal?: SoftSignal;
  // Set while this is the lead's current item in a "One at a time" stage.
  sequenceItem?: SequenceItem;
  onSuccess: (lastSoftSignal: SoftSignal, withdrawInterest?: boolean) => void;
};

const SoftSignals: React.FC<SoftSignalsProps> = (props) => {
  const {
    className,
    dataRoomNodeId,
    lastSoftSignal,
    sequenceItem,
    dataRoomId,
    dealRoomId,
    onSuccess,
  } = props;

  const { isLastItem } = sequenceItem || {};

  const [unsureModal, unsureModalRef] = useCallbackRef<ModalRef>();
  const [dislikeModal, dislikeModalRef] = useCallbackRef<ModalRef>();

  const classes = classNames(
    'soft-signals',
    { 'soft-signals--sequence': !!sequenceItem },
    className,
  );

  const { t } = useTranslation();

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const getMixpanelProps = useGetMixpanelProps();

  const renderButton = useCallback(
    (softSignal: SoftSignal, onClick: () => void, label?: string) => {
      const applied = !!lastSoftSignal && softSignal === lastSoftSignal;
      const disabled = !!lastSoftSignal && softSignal !== lastSoftSignal;

      return (
        <SoftSignalButton
          softSignal={softSignal}
          disabled={disabled}
          applied={applied}
          onClick={onClick}
        >
          {label}
        </SoftSignalButton>
      );
    },
    [lastSoftSignal],
  );

  // On the current item this releases the next one, or completes the sequence on the last.
  const onLike = useCallback(async () => {
    const softSignal = SOFT_SIGNALS.LIKE;

    mixpanelService.softSignalInitiated(
      getMixpanelProps({
        dataRoomId,
        documentId: dataRoomNodeId,
        dealId: dealRoomId,
        reaction: t('interested'),
      }) as SoftSignalInitiatedEventProps,
    );

    const request = api.dataRoom.nodes.softSignals(dataRoomNodeId, {
      softSignal,
    });

    onSuccess(softSignal);

    if (!sequenceItem) return;

    try {
      await request;

      popSuccess(
        t(isLastItem ? 'interestSentToBroker' : 'nextDocumentUnlocked'),
      );
    } catch (e) {
      popServerError(e);
    }
  }, [
    dataRoomNodeId,
    dataRoomId,
    dealRoomId,
    getMixpanelProps,
    isLastItem,
    onSuccess,
    popServerError,
    popSuccess,
    sequenceItem,
    t,
  ]);

  const modals = (
    <>
      <UnsureSignalModal
        ref={unsureModalRef}
        dataRoomNodeId={dataRoomNodeId}
        dataRoomId={dataRoomId}
        dealRoomId={dealRoomId}
        onSuccess={onSuccess}
      />
      <DislikeSignalModal
        ref={dislikeModalRef}
        dataRoomNodeId={dataRoomNodeId}
        dataRoomId={dataRoomId}
        dealRoomId={dealRoomId}
        onSuccess={onSuccess}
      />
    </>
  );

  if (sequenceItem) {
    return (
      <div className={classes}>
        <div className="soft-signals__copy">
          <p className="soft-signals__title">
            {t(
              isLastItem
                ? 'interestedInThisBusiness'
                : 'readyForTheNextDocument',
            )}
          </p>
          <p className="soft-signals__subtitle">
            {t(
              isLastItem
                ? 'lastDocumentSharedWithYouForNow'
                : 'nextDocumentUnlocksOnceYouRespond',
            )}
          </p>
        </div>

        <div className="soft-signals__actions">
          {renderButton(
            SOFT_SIGNALS.LIKE,
            onLike,
            t(isLastItem ? 'yesIAmInterested' : 'yesContinue'),
          )}
          {renderButton(
            SOFT_SIGNALS.UNSURE,
            unsureModal?.open,
            t('iHaveAQuestion'),
          )}
          {renderButton(
            SOFT_SIGNALS.DISLIKE,
            dislikeModal?.open,
            t('notInterested'),
          )}
        </div>

        {modals}
      </div>
    );
  }

  return (
    <div className={classes}>
      <p>{t('softSignalText')}</p>

      <div>
        {renderButton(SOFT_SIGNALS.LIKE, onLike)}
        {renderButton(SOFT_SIGNALS.UNSURE, unsureModal?.open)}
        {renderButton(SOFT_SIGNALS.DISLIKE, dislikeModal?.open)}
      </div>

      {modals}
    </div>
  );
};

export default SoftSignals;
